import { getDb, writeMeta, readMeta } from '../offline/db';
import { isOnline } from '../offline/network';
import { HttpClient, type OfflineCache } from './http';
import {
    currentTenantId,
    currentToken,
    isCurrentToken,
    useSessionStore,
    type Impersonation,
    type Session,
} from './session';

/**
 * Basis-URL van de API.
 *
 * Laravel serveert de app en de API op hetzelfde adres, dus standaard is dat gewoon
 * `/api/v1`. `VITE_API_URL` is er alleen voor wie de app bewust tegen een andere
 * server wil laten praten.
 */
export const API_URL = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/+$/, '');

const CACHE_PREFIX = 'http-cache:';

/**
 * Van wie en welk bedrijf bewaarde gegevens zijn: `<persoon>:<bedrijf>`.
 *
 * `null` betekent: niets bewaren en niets uit de bewaarde gegevens lezen. Dat
 * geldt zonder sessie, zonder omgeving (een platformbeheerder kijkt over alle
 * bedrijven heen) en tijdens het meekijken - wat een beheerder dan ziet is van
 * een ander en hoort niet in de opslag van dit apparaat.
 */
export function cacheScopeOf(state: {
    session: Session | null;
    impersonation: Impersonation | null;
}): string | null {
    const { session, impersonation } = state;

    if (!session?.tenant || impersonation) {
        return null;
    }

    return `${session.user.uuid}:${session.tenant.uuid}`;
}

export function currentCacheScope(): string | null {
    return cacheScopeOf(useSessionStore.getState());
}

/** Hoort deze sleutel (`<scope>|<adres>`) bij de sessie van nu? */
function belongsToCurrentScope(key: string): boolean {
    const scope = currentCacheScope();

    return scope !== null && key.startsWith(`${scope}|`);
}

/**
 * Offline cache voor GET-verzoeken: antwoorden gaan in de `meta`-tabel en
 * worden zonder netwerk uitgeserveerd. Grote domeindata (templates, keuringen)
 * hoort in de eigen Dexie-tabellen, niet hier.
 *
 * De sleutel draagt de scope van de sessie bij de start van het verzoek. Is de
 * sessie intussen gewisseld, dan wordt er niets gelezen of geschreven: een
 * antwoord van de vorige persoon of het vorige bedrijf komt nergens terecht.
 */
export const dexieOfflineCache: OfflineCache = {
    async read(key) {
        if (!belongsToCurrentScope(key)) {
            return undefined;
        }

        return readMeta(getDb(), CACHE_PREFIX + key);
    },
    async write(key, value) {
        if (!belongsToCurrentScope(key)) {
            return;
        }

        await writeMeta(getDb(), CACHE_PREFIX + key, value);
    },
};

/**
 * Gooit elk bewaard GET-antwoord weg dat niet bij `scope` hoort; `null` gooit
 * alles weg. Ook antwoorden uit de tijd dat de sleutel nog geen scope had.
 */
export async function forgetCachedResponses(scope: string | null): Promise<void> {
    const keep = scope === null ? null : `${CACHE_PREFIX}${scope}|`;

    await getDb()
        .meta.where('key')
        .startsWith(CACHE_PREFIX)
        .and((row) => keep === null || !row.key.startsWith(keep))
        .delete();
}

/**
 * De gedeelde API-client. Features gebruiken de clients uit `entities/*`;
 * alleen die entity-clients praten rechtstreeks met dit object.
 */
export const api = new HttpClient({
    baseUrl: API_URL,
    getToken: currentToken,
    getTenantId: currentTenantId,
    getDeviceId: () => useSessionStore.getState().session?.device.uuid ?? null,
    isOnline,
    offlineCache: dexieOfflineCache,
    cacheScope: currentCacheScope,
    onUnauthorized: (error, { token }) => {
        /*
         * Alleen een 401 op het token van de sessie van nu telt. Een token dat
         * net is vervangen (wisselen, verlengen) of het tijdelijke token van een
         * impersonatie zegt niets over de eigen sessie.
         */
        if (!isCurrentToken(token)) {
            return;
        }

        /*
         * Het token werkt niet meer. De sessie eindigt op dit apparaat; de guard
         * stuurt je naar het inlogscherm, behalve op een publieke pagina - die
         * heeft geen sessie nodig. Is het apparaat ingetrokken, dan zegt het
         * inlogscherm dat erbij.
         */
        void useSessionStore
            .getState()
            .signOut(
                error.problem.type.endsWith('/device-unavailable')
                    ? 'device-unavailable'
                    : 'expired',
            );
    },
});
